import React, { useEffect } from "react";
import { ArrowDown, ArrowUp, Columns3 } from "lucide-react";

import MultiSelectFilter from "../Common/Filters/MultiSelectFilter";
import TablePagination from "./TablePagination";
import { useTranslation } from "../../context/Internationalization";
import { Checkbox } from "../../shadcn/components/ui/checkbox";
import {
  Table as ShadcnTable,
  TableBody,
  TableHead,
  TableHeader,
  TableHiddenColumnsContext,
  TableRow,
} from "../../shadcn/components/ui/table";
import { TableSortButton } from "../../shadcn/components/ui/table-sort-button";
import { cn } from "../../shadcn/lib/utils";
import { Card } from "../../shadcn/components/ui/card";

const isColumnHideable = (column) => {
  const hasTextLabel =
    typeof column.label === "string" || typeof column.label === "number";
  return (
    column.hideable !== false &&
    hasTextLabel &&
    String(column.label).trim().length > 0
  );
};

const areColumnIdsEqual = (currentIds, nextIds) =>
  currentIds.length === nextIds.length &&
  currentIds.every((id, index) => id === nextIds[index]);

function EnhancedTableHead({
  columns,
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  isSelectable,
  selectAllAriaLabel,
}) {
  return (
    <TableHeader>
      <TableRow>
        {isSelectable ? (
          <TableHead className="w-12 px-4">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onCheckedChange={(checked) =>
                onSelectAllClick({ target: { checked } })
              }
              aria-label={selectAllAriaLabel || "select all rows"}
            />
          </TableHead>
        ) : null}
        {columns.map((headCell) => {
          const active = orderBy === headCell.id;
          const sortable = headCell.sortable !== false;
          const SortIcon = order === "desc" ? ArrowDown : ArrowUp;
          return (
            <TableHead
              key={headCell.id}
              aria-sort={
                active
                  ? order === "desc"
                    ? "descending"
                    : "ascending"
                  : undefined
              }
              className={cn(
                "px-4 text-muted-foreground",
                headCell.numeric && "text-right",
                headCell.disablePadding && "px-0"
              )}
              style={{ width: headCell.width, minWidth: headCell.minWidth }}
            >
              {sortable ? (
                <TableSortButton
                  active={active}
                  className="-ml-3 font-medium"
                  onClick={(event) => onRequestSort(event, headCell.id)}
                >
                  {headCell.label}
                  {active && <SortIcon aria-hidden="true" />}
                </TableSortButton>
              ) : (
                headCell.label
              )}
            </TableHead>
          );
        })}
      </TableRow>
    </TableHeader>
  );
}

function Table({
  rows,
  columns,
  rowElement,
  onSort,
  order,
  orderBy,
  isSelectable,
  onAllSelected = () => {},
  resetSelections = false,
  selectionResetKey,
  selectAllAriaLabel,
  tableAriaLabel,
  minWidth,
  className,
  disableAutoColumnSelection = false,
  stickyHeader = false,
  pagination,
}) {
  const [selected, setSelected] = React.useState([]);
  const [allSelected, setAllSelected] = React.useState(false);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] =
    React.useState(false);
  const tableContainerRef = React.useRef(null);
  const { t = (key) => key } = useTranslation();
  const columnOptions = React.useMemo(
    () =>
      columns.filter(isColumnHideable).map((column) => ({
        value: column.id,
        label: String(column.label),
      })),
    [columns]
  );
  const hideableColumnIds = React.useMemo(
    () => columnOptions.map((column) => column.value),
    [columnOptions]
  );
  const previousHideableColumnIds = React.useRef(hideableColumnIds);
  const [visibleColumnIds, setVisibleColumnIds] =
    React.useState(hideableColumnIds);
  const effectiveVisibleColumnIds = React.useMemo(
    () =>
      visibleColumnIds.filter((columnId) =>
        hideableColumnIds.includes(columnId)
      ),
    [hideableColumnIds, visibleColumnIds]
  );
  const canSelectColumns =
    !disableAutoColumnSelection && columnOptions.length > 1;
  const hasHiddenColumns =
    effectiveVisibleColumnIds.length < hideableColumnIds.length;

  const checkHorizontalOverflow = React.useCallback(() => {
    const wrapper = tableContainerRef.current;
    // The shadcn Table renders its own scrolling container inside ours.
    const container =
      wrapper?.querySelector('[data-slot="table-container"]') || wrapper;
    setHasHorizontalOverflow(
      Boolean(container && container.scrollWidth > container.clientWidth + 1)
    );
  }, []);

  const handleRequestSort = (event, property) => {
    if (onSort) {
      const isAsc = orderBy === property && order === "asc";
      onSort({ direction: isAsc ? "desc" : "asc", key: property });
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(rows.map((n) => n._id));
      setAllSelected(true);
      return;
    }
    setSelected([]);
    setAllSelected(false);
  };

  const handleClick = (event, id) => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    );
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  useEffect(() => {
    onAllSelected(allSelected);
  }, [allSelected]);

  useEffect(() => {
    if (resetSelections) {
      setSelected([]);
      setAllSelected(false);
    }
  }, [resetSelections]);

  useEffect(() => {
    if (selectionResetKey !== undefined) {
      setSelected([]);
      setAllSelected(false);
    }
  }, [selectionResetKey]);

  useEffect(() => {
    const previousColumnIds = previousHideableColumnIds.current;

    setVisibleColumnIds((currentVisibleColumnIds) => {
      const retainedColumnIds = currentVisibleColumnIds.filter((columnId) =>
        hideableColumnIds.includes(columnId)
      );
      const addedColumnIds = hideableColumnIds.filter(
        (columnId) => !previousColumnIds.includes(columnId)
      );
      const nextVisibleColumnIds = [...retainedColumnIds, ...addedColumnIds];

      if (!nextVisibleColumnIds.length && hideableColumnIds.length) {
        nextVisibleColumnIds.push(hideableColumnIds[0]);
      }

      return areColumnIdsEqual(currentVisibleColumnIds, nextVisibleColumnIds)
        ? currentVisibleColumnIds
        : nextVisibleColumnIds;
    });

    previousHideableColumnIds.current = hideableColumnIds;
  }, [hideableColumnIds]);

  useEffect(() => {
    if (!canSelectColumns) {
      setHasHorizontalOverflow(false);
      return undefined;
    }

    const container = tableContainerRef.current;
    if (!container) return undefined;

    checkHorizontalOverflow();
    window.addEventListener("resize", checkHorizontalOverflow);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(checkHorizontalOverflow);

    if (resizeObserver) {
      resizeObserver.observe(container);
      const tableElement = container.querySelector("table");
      if (tableElement) resizeObserver.observe(tableElement);
    }

    return () => {
      window.removeEventListener("resize", checkHorizontalOverflow);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [
    canSelectColumns,
    checkHorizontalOverflow,
    columns,
    rows,
    visibleColumnIds,
  ]);

  // Rows are rendered by pages, so hidden columns are skipped by position:
  // every TableRow inside this table leaves those cells out.
  const hiddenColumns = React.useMemo(() => {
    if (!canSelectColumns) return new Set();
    const offset = isSelectable ? 1 : 0;
    return new Set(
      columns.flatMap((column, columnIndex) =>
        isColumnHideable(column) &&
        !effectiveVisibleColumnIds.includes(column.id)
          ? [columnIndex + offset]
          : []
      )
    );
  }, [canSelectColumns, columns, effectiveVisibleColumnIds, isSelectable]);

  const handleVisibleColumnsChange = ({ target: { value } }) => {
    if (value.length) setVisibleColumnIds(value);
  };

  return (
    <div className="w-full">
      {canSelectColumns && (hasHorizontalOverflow || hasHiddenColumns) ? (
        <div className="mb-5 flex justify-end">
          <MultiSelectFilter
            name="visibleTableColumns"
            label={t("table_columns")}
            options={columnOptions}
            value={effectiveVisibleColumnIds}
            onChange={handleVisibleColumnsChange}
            icon={<Columns3 aria-hidden="true" />}
            align="end"
          />
        </div>
      ) : null}
      {/* With stickyHeader the table box scrolls by itself (up to 440px), so
          the header cells can stick to its top. */}
      <Card
        ref={tableContainerRef}
        data-sticky-header={stickyHeader ? "" : undefined}
        className={cn(
          "mb-4 w-full gap-0 overflow-hidden py-0",
          stickyHeader &&
            "[&>[data-slot=table-container]]:max-h-[440px] [&>[data-slot=table-container]]:overflow-auto [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-card"
        )}
      >
        <TableHiddenColumnsContext.Provider value={hiddenColumns}>
        <ShadcnTable
          aria-label={tableAriaLabel}
          className={cn("w-full", className)}
          style={minWidth ? { minWidth } : undefined}
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            columns={columns}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            isSelectable={isSelectable}
            selectAllAriaLabel={selectAllAriaLabel}
          />
          <TableBody>
            {rows.map((row, index) =>
              rowElement(row, index, isSelected(row._id), handleClick)
            )}
          </TableBody>
        </ShadcnTable>
        </TableHiddenColumnsContext.Provider>
      </Card>
      {/* Page controls: { page, rowsPerPage, total, onPageChange,
          onRowsPerPageChange?, disabled? }. Hidden when there is nothing to page. */}
      {pagination && (rows.length > 0 || pagination.total > 0) ? (
        <TablePagination {...pagination} />
      ) : null}
    </div>
  );
}

export default Table;
